import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback } from 'react';

import { settings } from '@/lib/db';

import { DEFAULT_CURRENCY_REMINDERS, type ExpiryReminder } from './currency';
import { LOGBOOK_CURRENCY_REMINDERS_KEY } from './settings-key';

/**
 * The pilot-edited expiry reminders, live, defaulting to
 * `DEFAULT_CURRENCY_REMINDERS` until the list is touched for the first time —
 * same "`null` means not open yet, defaults fill in the rest" convention as
 * `useSettings`.
 */
export function useCurrencyReminders(): readonly ExpiryReminder[] | null {
  return useLiveQuery(
    () =>
      settings.get<ExpiryReminder[]>(LOGBOOK_CURRENCY_REMINDERS_KEY, [
        ...DEFAULT_CURRENCY_REMINDERS,
      ]),
    [],
    null,
  );
}

export function saveCurrencyReminders(reminders: readonly ExpiryReminder[]): Promise<void> {
  return settings.set(LOGBOOK_CURRENCY_REMINDERS_KEY, reminders);
}

/** One field of one reminder, for a single input's `onChange` — mirrors `useSetting`'s per-field ergonomics. */
export function useUpdateReminder(): (id: string, patch: Partial<ExpiryReminder>) => void {
  const reminders = useCurrencyReminders();
  return useCallback(
    (id: string, patch: Partial<ExpiryReminder>) => {
      const current = reminders ?? DEFAULT_CURRENCY_REMINDERS;
      const next = current.map((reminder) =>
        reminder.id === id ? { ...reminder, ...patch } : reminder,
      );
      void saveCurrencyReminders(next);
    },
    [reminders],
  );
}
